import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import DOMPurify from "dompurify";
import api from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import LikeButton from "@/components/LikeButton";
import RegionBlocked from "@/components/RegionBlocked";
import { LockBadge, LockedNotice, AccessModal } from "@/components/LockedContent";
import { useSeo } from "@/lib/useSeo";
import { getClientId } from "@/lib/clientId";

// Module-scope guard: survives React StrictMode's double-mount even when
// component state doesn't. Ensures POST /view fires at most once per slug per tab.
const viewedThisTab = new Set();

// Read-time reporter shared across the page's lifecycle events
function useReadTimeTracker(slug, isLockedPreview) {
  const startRef = useRef(null);
  const lockedRef = useRef(true); // teaser views never count as reads
  const accumulatedRef = useRef(0);
  const sentRef = useRef(false);

  useEffect(() => {
    lockedRef.current = Boolean(isLockedPreview);
  }, [isLockedPreview]);

  useEffect(() => {
    if (!slug || isLockedPreview) return;
    startRef.current = performance.now();
    accumulatedRef.current = 0;
    sentRef.current = false;

    const nowSec = () => {
      const t = startRef.current == null ? 0 : (performance.now() - startRef.current) / 1000;
      return Math.max(0, t);
    };

    const flush = (final = false) => {
      if (lockedRef.current) return;
      const secs = accumulatedRef.current + nowSec();
      if (final && sentRef.current) return;
      if (secs < 2) return;
      sentRef.current = true;
      const body = JSON.stringify({ seconds: secs, client_id: getClientId() });
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/posts/${slug}/read-time`;
      // sendBeacon works during unload; falls back to fetch keepalive
      if (navigator.sendBeacon) {
        try {
          navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
          return;
        } catch (_) { /* fall through */ }
      }
      fetch(url, { method: "POST", body, headers: { "Content-Type": "application/json" }, keepalive: true }).catch((err) => console.error("Failed to send read-time beacon:", err));
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        // pause: bank time so far
        accumulatedRef.current += nowSec();
        startRef.current = null;
        flush(false);
      } else if (document.visibilityState === "visible") {
        startRef.current = performance.now();
        sentRef.current = false; // allow another flush next time tab hides / unloads
      }
    };
    const onPageHide = () => flush(true);

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onPageHide);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onPageHide);
      flush(true); // route change / unmount
    };
  }, [slug, isLockedPreview]);
}

export default function JournalPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [modal, setModal] = useState(false);
  const mountedRef = useRef(true);

  useReadTimeTracker(post ? slug : null, post ? Boolean(post.locked_preview) : true);

  useSeo({
    title: post?.title,
    description: post?.excerpt,
    image: post?.cover_image,
    type: "article",
  });

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    setLoading(true);
    const doWork = async () => {
      const key = `gv_view_${slug}`;
      let shouldBump = false;
      if (typeof window !== "undefined" && !viewedThisTab.has(slug) && sessionStorage.getItem(key) !== "1") {
        shouldBump = true;
        viewedThisTab.add(slug);
        sessionStorage.setItem(key, "1"); // sync guard for StrictMode double-effect
      }
      try {
        // Fire POST FIRST when eligible so the subsequent GET returns the
        // already-incremented view count.
        if (shouldBump) {
          try { await api.post(`/posts/${slug}/view`); } catch (err) { console.error("Failed to record view:", err); }
        }
        const { data } = await api.get(`/posts/${slug}`);
        if (mountedRef.current) setPost(data);
      } catch (e) {
        if (e.response?.status === 451 && mountedRef.current) setBlocked(true);
        else if (e.response?.status === 404 && mountedRef.current) setNotFound(true);
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    };
    doWork();
  }, [slug]);

  if (loading) {
    return (
      <PublicLayout>
        <div className="pt-40 pb-32 text-center text-[#5C6670]">Loading…</div>
      </PublicLayout>
    );
  }
  if (blocked) return <RegionBlocked />;
  if (notFound || !post) {
    return (
      <PublicLayout>
        <div className="pt-40 pb-32 text-center">
          <p className="font-serif text-3xl italic text-[#5C6670]">This entry has drifted out to sea.</p>
          <Link to="/journal" className="mt-6 inline-block gv-link text-sm tracking-[0.2em] uppercase text-[#C5A059]">Back to the journal</Link>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <AccessModal open={modal} onClose={() => setModal(false)} />
      <article data-testid="post-page" className="pt-36 pb-32 px-6 md:px-12 bg-[#FAFAFA]">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <Link to="/journal" className="text-xs tracking-[0.22em] uppercase text-[#5C6670] hover:text-[#C5A059]" data-testid="post-back">← Journal</Link>
            <div className="mt-8 flex items-center gap-3 text-xs tracking-[0.22em] uppercase text-[#5C6670]">
              <span className="text-[#C5A059]">{post.tag || "Essay"}</span>
              <span className="w-6 h-px bg-[#E5E5E5]" />
              <span>{new Date(post.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}</span>
              {post.locked && <LockBadge testId="post-lock" />}
            </div>
            <h1 className="font-serif text-4xl md:text-6xl mt-6 text-[#1A1F24] leading-[1.1]">{post.title}</h1>
            <p className="mt-6 text-[#5C6670] text-lg italic font-light max-w-2xl">{post.excerpt}</p>
            <div className="mt-8">
              <LikeButton slug={slug} initialViews={post.views || 0} />
            </div>
          </motion.div>
        </div>
        <div className="max-w-5xl mx-auto mt-16">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 0.2 }}>
            {post.cover_image && (
              <img src={post.cover_image} alt={post.title} className="w-full h-[420px] md:h-[560px] object-cover" />
            )}
          </motion.div>
        </div>
        <div className="max-w-2xl mx-auto mt-16 gv-prose">
          {post.locked_preview ? (
            <LockedNotice onRequest={() => setModal(true)} label="This is an exclusive entry" />
          ) : /<[a-z][\s\S]*>/i.test(post.content) ? (
            <div data-testid="post-content-html" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }} />
          ) : (
            post.content.split(/\n\n+/).map((para, i) => <p key={`${slug}-para-${i}`}>{para}</p>)
          )}
        </div>
        <div className="max-w-2xl mx-auto mt-20 pt-12 border-t border-[#E5E5E5] text-center">
          <p className="font-serif italic text-xl text-[#5C6670]">— Gemma Veda</p>
          <Link to="/journal" className="mt-8 inline-block gv-link text-sm tracking-[0.2em] uppercase text-[#C5A059]" data-testid="post-more">More entries</Link>
        </div>
      </article>
    </PublicLayout>
  );
}
